import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Info, Mail, AlertTriangle, Send, Menu, X, LayoutGrid, Search } from "lucide-react";
import { useData } from "../data/DataContext";

const links = [
  { to: "/", label: "Home", icon: Home },
  { to: "/yono-all-games", label: "Yono All Games", icon: LayoutGrid },
  { to: "/about", label: "About", icon: Info },
  { to: "/contact", label: "Contact", icon: Mail },
  { to: "/disclaimer", label: "Disclaimer", icon: AlertTriangle },
  { to: "__telegram__", label: "Telegram", icon: Send, external: true },
];

const NavLink = ({ item, mobile, onClick, active }) => {
  const Icon = item.icon;
  const cls = `flex items-center gap-2 font-semibold text-[13px] lg:text-sm whitespace-nowrap transition-colors duration-200 ${
    active ? "text-[#ffb224]" : "text-emerald-50/85 hover:text-[#ffb224]"
  } ${mobile ? "px-4 py-3 rounded-xl hover:bg-white/5 text-base" : ""}`;
  if (item.external) {
    return <a href={item.to} target="_blank" rel="noreferrer" className={cls} onClick={onClick}><Icon size={17} /> {item.label}</a>;
  }
  return <Link to={item.to} className={cls} onClick={onClick}><Icon size={17} /> {item.label}</Link>;
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [q, setQ] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { telegramLink, logo, settings } = useData();
  const navLinks = links.map((l) => (l.external ? { ...l, to: telegramLink } : l));

  const submit = (e) => {
    e.preventDefault();
    navigate(`/?q=${encodeURIComponent(q.trim())}#games`);
    setSearch(false);
  };

  return (
    <header data-testid="navbar" className="fixed top-0 inset-x-0 z-50 bg-[#013013]/95 backdrop-blur-md border-b border-[#1c5f35]">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 h-16 flex items-center gap-3">
        <button
          data-testid="navbar-menu-btn"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-white hover:bg-white/10 transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>

        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group shrink-0">
          <img src={logo} alt={`${settings.siteName} logo`} className="w-10 h-10 rounded-xl object-cover shadow-md ring-1 ring-[#1c5f35] group-hover:scale-105 transition-transform duration-200" />
          <span className="text-lg sm:text-xl font-extrabold tracking-tight text-white whitespace-nowrap">
            Maha <span className="text-[#ffb224]">Games</span> Apps
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-4 lg:gap-6 ml-auto">
          {navLinks.map((l) => <NavLink key={l.label} item={l} active={location.pathname === l.to} />)}
        </nav>

        <button
          data-testid="navbar-search-btn"
          onClick={() => setSearch(!search)}
          aria-label="Search"
          className="ml-auto md:ml-2 w-10 h-10 flex items-center justify-center rounded-xl text-white hover:bg-white/10 transition-colors"
        >
          <Search size={21} />
        </button>
      </div>

      {search && (
        <form onSubmit={submit} className="border-t border-[#1c5f35] bg-[#073c1b] px-4 py-3">
          <div className="max-w-2xl mx-auto flex gap-2">
            <input
              autoFocus
              data-testid="navbar-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a Yono game, e.g. Rummy 91"
              className="flex-1 bg-[#013013] border border-[#1c5f35] rounded-xl px-4 py-2.5 text-white placeholder:text-emerald-100/40 outline-none focus:border-[#ffb224]"
            />
            <button data-testid="navbar-search-submit" className="bg-[#ffb224] text-[#062a13] font-bold px-5 rounded-xl border-b-[3px] border-[#b47400] active:border-b-0 active:translate-y-[3px] transition-all">Go</button>
          </div>
        </form>
      )}

      {open && (
        <nav data-testid="navbar-mobile-menu" className="md:hidden border-t border-[#1c5f35] bg-[#073c1b] px-3 py-2 flex flex-col">
          {navLinks.map((l) => <NavLink key={l.label} item={l} mobile active={location.pathname === l.to} onClick={() => setOpen(false)} />)}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
